import React, { useState, useEffect, useCallback } from "react";
import MetaTags from "react-meta-tags";
import { Container, Row, Col, Spinner, Alert } from "reactstrap";
import { useTranslation } from "react-i18next";
import cookieHelper from "helpers/getCookieData";
import { get } from "../../helpers/api_helper";

// Components
import QuickActionsToolbar from "./components/QuickActionsToolbar";
import ExecutiveKpiCards from "./components/ExecutiveKpiCards";
import RevenueTrendChart from "./components/RevenueTrendChart";
import ProcedureBreakdownChart from "./components/ProcedureBreakdownChart";
import DoctorTasksWidget from "./components/DoctorTasksWidget";
import LiveWaitingRoomWidget from "./components/LiveWaitingRoomWidget";
import DoctorPerformanceTable from "./components/DoctorPerformanceTable";
import RecentCollectionsFeed from "./components/RecentCollectionsFeed";

const Dashboard = (props) => {
  const { t } = useTranslation();

  const [clinic, setClinic] = useState(null);
  const [range, setRange] = useState("this_month");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    range: "this_month",
    date_range_label: "",
    financials: {
      total_billed: 0,
      total_collected: 0,
      pending_dues: 0,
      collection_rate: 100,
      revenue_growth: 0,
      payment_modes: {}
    },
    clinical: {
      total_appointments: 0,
      visited_appointments: 0,
      completed_appointments: 0,
      in_chair_appointments: 0,
      waiting_appointments: 0
    },
    patients: {
      total_patients: 0,
      new_patients: 0,
      returning_patients: 0
    },
    charts: {
      revenue_trend: { categories: [], series: [] },
      procedure_distribution: { labels: [], series: [] }
    },
    live_waiting_queue: [],
    doctor_matrix: [],
    recent_transactions: []
  });

  // Verify clinic session
  useEffect(() => {
    try {
      const clinicCookie = cookieHelper.getCookie("_c");
      if (clinicCookie && atob(clinicCookie) !== "undefined") {
        const parsed = JSON.parse(atob(clinicCookie));
        setClinic(parsed);
      } else {
        props.history.push("/clinics");
      }
    } catch (e) {
      props.history.push("/clinics");
    }
  }, [props.history]);

  // Fetch Executive Summary Data
  const fetchDashboardData = useCallback(async (selectedRange = range) => {
    setLoading(true);
    setError(null);
    try {
      const res = await get(`/reports/executive-dashboard-summary?range=${selectedRange}`);
      if (res && res.success && res.body) {
        setDashboardData(res.body);
      }
    } catch (err) {
      console.error("Error fetching dashboard summary:", err);
      setError(t("Failed to load live analytical data."));
    } finally {
      setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    fetchDashboardData(range);
  }, [fetchDashboardData, range]);

  const handleRangeChange = (newRange) => {
    setRange(newRange);
  };

  const handleRefresh = () => {
    fetchDashboardData(range);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("Executive Command Center")} | {process.env.REACT_APP_TITLE}</title>
        </MetaTags>

        <Container fluid>
          {/* Top Page Header */}
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <div>
              <h4 className="mb-1 text-dark fw-bold d-flex align-items-center gap-2">
                <i className="bx bx-clinic text-primary font-size-22" />
                {clinic?.clinic_name || t("Clinic Executive Command Center")}
              </h4>
              <p className="text-muted mb-0 font-size-13">
                {t("Comprehensive analytical intelligence, revenue trajectory, and clinical operations.")}
                {dashboardData.date_range_label && (
                  <span className="badge bg-soft-primary text-primary ms-2 font-size-11">
                    📅 {dashboardData.date_range_label}
                  </span>
                )}
              </p>
            </div>
          </div>

          {error && (
            <Alert color="danger" className="d-flex align-items-center justify-content-between">
              <span>{error}</span>
              <button className="btn btn-sm btn-outline-danger" onClick={handleRefresh}>
                {t("Retry")}
              </button>
            </Alert>
          )}

          {/* 1. Quick Actions & Range Selector Toolbar */}
          <QuickActionsToolbar
            range={range}
            onRangeChange={handleRangeChange}
            onRefresh={handleRefresh}
            loading={loading}
          />

          {/* 2. Executive Hero KPI Metric Cards */}
          <ExecutiveKpiCards
            financials={dashboardData.financials}
            clinical={dashboardData.clinical}
            patients={dashboardData.patients}
          />

          {/* 3. Core Analytics Charts Grid */}
          <Row className="g-3 mb-2">
            <Col lg="8">
              <RevenueTrendChart chartData={dashboardData.charts.revenue_trend} />
            </Col>
            <Col lg="4">
              <ProcedureBreakdownChart chartData={dashboardData.charts.procedure_distribution} />
            </Col>
          </Row>

          {/* 4. Operational Cockpit: Tasks Hub & Live Waiting Room Queue */}
          <Row className="g-3 mb-2">
            <Col lg="5">
              <DoctorTasksWidget />
            </Col>
            <Col lg="7">
              <LiveWaitingRoomWidget queue={dashboardData.live_waiting_queue} />
            </Col>
          </Row>

          {/* 5. Doctor Matrix & Recent Financial Collections */}
          <Row className="g-3 mb-4">
            <Col lg="6">
              <DoctorPerformanceTable doctors={dashboardData.doctor_matrix} />
            </Col>
            <Col lg="6">
              <RecentCollectionsFeed transactions={dashboardData.recent_transactions} />
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
